import { Link } from "react-router-dom";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import SEO from "@/components/SEO";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { ArrowRight, ArrowLeft, Package, Battery, Gauge, Shield } from "lucide-react";
import t600Img from "@/assets/robots/pudu-t600-updated.png";
import t600UnderrideImg from "@/assets/robots/pudu-t600-underride.png";
import followModeImg from "@/assets/robots/follow-mode-demo.webp";
import actionVideo from "@/assets/robots/t600-t300-action.mov";

const PuduT600 = () => {
  return (
    <>
      <SEO 
        title="PUDU T600 - Heavy-Payload Industrial Delivery Robot | Neil AI"
        description="Industrial delivery robot with 600kg payload capacity, 12-hour battery life, and built-in touch screen. Features VSLAM + LiDAR navigation, multi-floor capability, and VDA5050 protocol support."
        keywords="PUDU T600, industrial robot, warehouse robot, heavy payload robot, delivery robot, autonomous material handling, industrial automation"
      />
      
      <div className="min-h-screen bg-gradient-to-b from-background via-background to-primary/5">
        <Navigation />
        
        {/* Hero Section */}
        <section className="pt-44 pb-12 px-4 md:pt-48">
          <div className="container mx-auto">
            <Link to="/robots" className="inline-flex items-center text-primary hover:underline mb-6">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to All Robots
            </Link>
            
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div className="space-y-6">
                <Badge className="bg-primary/10 text-primary">Heavy-Payload Industrial Delivery Robot</Badge>
                <h1 className="text-2xl md:text-4xl lg:text-5xl font-orbitron font-bold break-words">
                  PUDU T600
                </h1>
                <p className="text-base md:text-lg lg:text-xl text-muted-foreground leading-relaxed">
                  Industrial-grade delivery robot with 600kg payload capacity, 12-hour battery life, and advanced navigation for seamless multi-floor operations.
                </p>
                
                <div className="grid grid-cols-2 gap-4 py-6">
                  <Card className="p-4 glass-effect border-primary/20">
                    <p className="text-2xl font-bold text-primary">600kg</p>
                    <p className="text-sm text-muted-foreground">Maximum Payload</p>
                  </Card>
                  <Card className="p-4 glass-effect border-primary/20">
                    <p className="text-2xl font-bold text-primary">12 Hours</p>
                    <p className="text-sm text-muted-foreground">Battery Life</p>
                  </Card>
                  <Card className="p-4 glass-effect border-primary/20">
                    <p className="text-2xl font-bold text-primary">Multi-Floor</p>
                    <p className="text-sm text-muted-foreground">Elevator Control</p>
                  </Card>
                  <Card className="p-4 glass-effect border-primary/20">
                    <p className="text-2xl font-bold text-primary">VDA5050</p>
                    <p className="text-sm text-muted-foreground">Protocol Support</p>
                  </Card>
                </div>

                <div className="flex flex-col sm:flex-row flex-wrap gap-4">
                  <Button size="lg" asChild className="glow-gold">
                    <a href="https://api.leadconnectorhq.com/widget/bookings/neilaipudurobotics" target="_blank" rel="noopener noreferrer">
                      Book a Demo <ArrowRight className="ml-2" />
                    </a>
                  </Button>
                  <Button asChild size="lg" variant="outline">
                    <a href="https://myascentium.com/ApplyNow/CommCredit?v=b106671d-0a80-40ca-aea6-5c888e426df6&s=dca75d8e-7184-4ed5-a7d0-f35ed9e35e86" target="_blank" rel="noopener noreferrer">
                      Apply for Financing
                    </a>
                  </Button>
                  <Button size="lg" variant="outline" asChild>
                    <Link to="/robot-recommender">Find Your Perfect Robot</Link>
                  </Button>
                </div>
              </div>

              <div className="relative">
                <div className="aspect-square bg-gradient-to-br from-primary/20 to-background rounded-2xl p-8 border border-primary/20">
                  <img 
                    src={t600Img} 
                    alt="PUDU T600" 
                    className="w-full h-full object-contain drop-shadow-2xl"
                  />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Tabs Section */}
        <section className="py-12 px-4">
          <div className="container mx-auto">
            <Tabs defaultValue="overview" className="w-full">
              <TabsList className="flex w-full max-w-3xl mx-auto mb-8 overflow-x-auto scrollbar-hide">
                <TabsTrigger value="overview" className="flex-shrink-0 text-xs sm:text-sm px-3 py-2">Overview</TabsTrigger>
                <TabsTrigger value="specs" className="flex-shrink-0 text-xs sm:text-sm px-3 py-2">Specs</TabsTrigger>
                <TabsTrigger value="applications" className="flex-shrink-0 text-xs sm:text-sm px-3 py-2">Applications</TabsTrigger>
                <TabsTrigger value="action" className="flex-shrink-0 text-xs sm:text-sm px-3 py-2">See It In Action</TabsTrigger>
              </TabsList>

              <TabsContent value="overview" className="space-y-8">
                <Card className="p-8 glass-effect border-primary/20">
                  <h2 className="text-3xl font-orbitron font-bold mb-6">Industrial-Grade Performance</h2>
                  <p className="text-lg text-muted-foreground mb-6">
                    The PUDU T600 is engineered for demanding industrial environments, offering exceptional payload capacity, extended battery life, and intelligent navigation. With support for the VDA5050 protocol and multi-floor operations, it seamlessly integrates into complex logistics workflows.
                  </p>
                  
                  <div className="grid md:grid-cols-2 gap-6 mt-8">
                    <div className="space-y-4">
                      <div className="flex items-start gap-4">
                        <Package className="w-8 h-8 text-primary flex-shrink-0" />
                        <div>
                          <h3 className="font-bold text-lg mb-2">600kg Payload Capacity</h3>
                          <p className="text-muted-foreground">Industry-leading load capacity handles heavy materials with ease, reducing trips and increasing efficiency.</p>
                        </div>
                      </div>
                      
                      <div className="flex items-start gap-4">
                        <Battery className="w-8 h-8 text-primary flex-shrink-0" />
                        <div>
                          <h3 className="font-bold text-lg mb-2">12-Hour Battery Life</h3>
                          <p className="text-muted-foreground">Extended runtime with 2-hour fast charging keeps operations running smoothly throughout the workday.</p>
                        </div>
                      </div>
                    </div>

                    <div className="space-y-4">
                      <div className="flex items-start gap-4">
                        <Gauge className="w-8 h-8 text-primary flex-shrink-0" />
                        <div>
                          <h3 className="font-bold text-lg mb-2">Built-in Touch Screen</h3>
                          <p className="text-muted-foreground">Intuitive interface for easy operation, task management, and real-time status monitoring.</p>
                        </div>
                      </div>
                      
                      <div className="flex items-start gap-4">
                        <Shield className="w-8 h-8 text-primary flex-shrink-0" />
                        <div>
                          <h3 className="font-bold text-lg mb-2">Ground Projection Safety</h3>
                          <p className="text-muted-foreground">Advanced safety system projects warning zones to protect personnel and prevent collisions.</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </Card>

                <Card className="p-8 glass-effect border-primary/20">
                  <h2 className="text-3xl font-orbitron font-bold mb-6">T600 Underride Variant</h2>
                  <div className="grid md:grid-cols-2 gap-8 items-center">
                    <div>
                      <img 
                        src={t600UnderrideImg} 
                        alt="PUDU T600 Underride" 
                        className="w-full object-contain rounded-lg"
                      />
                    </div>
                    <div className="space-y-4">
                      <Badge className="bg-primary/10 text-primary">Flexible & Efficient Industrial Platform</Badge>
                      <h3 className="text-2xl font-bold">Low-Profile Chassis Design</h3>
                      <p className="text-muted-foreground">
                        The T600 Underride features a compact 255mm height that navigates beneath racks and lifts them autonomously. This innovative design offers unmatched flexibility in material handling operations.
                      </p>
                      <ul className="space-y-3">
                        <li className="flex items-start gap-3">
                          <span className="text-primary text-xl">✓</span>
                          <span>Two operational modes: underride and standard delivery</span>
                        </li>
                        <li className="flex items-start gap-3">
                          <span className="text-primary text-xl">✓</span>
                          <span>Flexible configuration for diverse warehouse needs</span>
                        </li>
                        <li className="flex items-start gap-3">
                          <span className="text-primary text-xl">✓</span>
                          <span>Industrial-grade durability for 24/7 operations</span>
                        </li>
                        <li className="flex items-start gap-3">
                          <span className="text-primary text-xl">✓</span>
                          <span>On-premises deployment for data security</span>
                        </li>
                      </ul>
                    </div>
                  </div>
                </Card>

                <Card className="p-8 glass-effect border-primary/20">
                  <h2 className="text-3xl font-orbitron font-bold mb-6">Intelligent Follow Mode</h2>
                  <div className="grid md:grid-cols-2 gap-8 items-center">
                    <div>
                      <img 
                        src={followModeImg} 
                        alt="PUDU T600 Follow Mode in Action" 
                        className="w-full object-contain rounded-lg"
                      />
                    </div>
                    <div className="space-y-4">
                      <Badge className="bg-primary/10 text-primary">Hands-Free Operation</Badge>
                      <h3 className="text-2xl font-bold">Follow Workers Automatically</h3>
                      <p className="text-muted-foreground">
                        The T600's intelligent follow mode automatically tracks and follows workers throughout the facility, eliminating the need to push or drive heavy loads. Blue ground projection provides enhanced safety awareness for nearby personnel.
                      </p>
                      <ul className="space-y-3">
                        <li className="flex items-start gap-3">
                          <span className="text-primary text-xl">✓</span>
                          <span>Automatic worker tracking with real-time positioning</span>
                        </li>
                        <li className="flex items-start gap-3">
                          <span className="text-primary text-xl">✓</span>
                          <span>Blue ground projection for enhanced safety visibility</span>
                        </li>
                        <li className="flex items-start gap-3">
                          <span className="text-primary text-xl">✓</span>
                          <span>Reduces physical strain on warehouse personnel</span>
                        </li>
                        <li className="flex items-start gap-3">
                          <span className="text-primary text-xl">✓</span>
                          <span>Maintains safe following distance automatically</span>
                        </li>
                      </ul>
                    </div>
                  </div>
                </Card>

                <Card className="p-8 glass-effect border-primary/20 bg-gradient-to-br from-primary/5 to-background">
                  <h2 className="text-2xl font-orbitron font-bold mb-4">Key Benefits</h2>
                  <ul className="space-y-3">
                    <li className="flex items-start gap-3">
                      <span className="text-primary text-xl">✓</span>
                      <span>Significantly reduces manual labor costs in warehouse operations</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="text-primary text-xl">✓</span>
                      <span>Increases throughput with reliable 24/7 autonomous operations</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="text-primary text-xl">✓</span>
                      <span>Enhances safety with ground projection and collision avoidance</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="text-primary text-xl">✓</span>
                      <span>Seamless integration with existing warehouse management systems</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="text-primary text-xl">✓</span>
                      <span>Scalable solution that grows with your business needs</span>
                    </li>
                  </ul>
                </Card>
              </TabsContent>

              <TabsContent value="specs" className="space-y-6">
                <Card className="p-8 glass-effect border-primary/20">
                  <h2 className="text-3xl font-orbitron font-bold mb-6">Technical Specifications</h2>
                  
                  <div className="grid md:grid-cols-2 gap-8">
                    <div className="space-y-6">
                      <div>
                        <h3 className="text-xl font-bold mb-4 text-primary">Payload & Capacity</h3>
                        <div className="space-y-2 text-muted-foreground">
                          <p>• Maximum Payload: 600kg</p>
                          <p>• Rack Group Recognition: Intelligent load detection</p>
                          <p>• Flexible Configuration: Multiple load types supported</p>
                        </div>
                      </div>

                      <div>
                        <h3 className="text-xl font-bold mb-4 text-primary">Navigation Technology</h3>
                        <div className="space-y-2 text-muted-foreground">
                          <p>• VSLAM + LiDAR fusion navigation</p>
                          <p>• Multi-floor elevator control</p>
                          <p>• Autonomous obstacle avoidance</p>
                          <p>• Dynamic path planning</p>
                        </div>
                      </div>

                      <div>
                        <h3 className="text-xl font-bold mb-4 text-primary">Battery & Power</h3>
                        <div className="space-y-2 text-muted-foreground">
                          <p>• Battery Life: 12 hours continuous operation</p>
                          <p>• Charging Time: 2 hours fast charging</p>
                          <p>• Auto-charging capability</p>
                          <p>• Battery management system</p>
                        </div>
                      </div>

                      <div>
                        <h3 className="text-xl font-bold mb-4 text-primary">T600 Underride Specs</h3>
                        <div className="space-y-2 text-muted-foreground">
                          <p>• Height: 255mm (low-profile design)</p>
                          <p>• Payload: 600kg capacity</p>
                          <p>• Two operational modes available</p>
                          <p>• Rack lifting capability</p>
                        </div>
                      </div>
                    </div>

                    <div className="space-y-6">
                      <div>
                        <h3 className="text-xl font-bold mb-4 text-primary">User Interface</h3>
                        <div className="space-y-2 text-muted-foreground">
                          <p>• Built-in touch screen display</p>
                          <p>• Intuitive control interface</p>
                          <p>• Real-time status monitoring</p>
                          <p>• Task management dashboard</p>
                        </div>
                      </div>

                      <div>
                        <h3 className="text-xl font-bold mb-4 text-primary">Safety Features</h3>
                        <div className="space-y-2 text-muted-foreground">
                          <p>• Ground projection safety system</p>
                          <p>• 360° obstacle detection sensors</p>
                          <p>• Emergency stop functionality</p>
                          <p>• Collision prevention algorithms</p>
                          <p>• Safety-certified design</p>
                        </div>
                      </div>

                      <div>
                        <h3 className="text-xl font-bold mb-4 text-primary">Integration & Connectivity</h3>
                        <div className="space-y-2 text-muted-foreground">
                          <p>• VDA5050 protocol support</p>
                          <p>• WMS/ERP system integration</p>
                          <p>• Cloud-based fleet management</p>
                          <p>• API for custom integrations</p>
                          <p>• On-premises deployment option</p>
                        </div>
                      </div>

                      <div>
                        <h3 className="text-xl font-bold mb-4 text-primary">Durability</h3>
                        <div className="space-y-2 text-muted-foreground">
                          <p>• Industrial-grade construction</p>
                          <p>• 24/7 continuous operation capability</p>
                          <p>• Dust and debris resistance</p>
                          <p>• Long service life design</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </Card>
              </TabsContent>

              <TabsContent value="applications" className="space-y-6">
                <Card className="p-8 glass-effect border-primary/20">
                  <h2 className="text-3xl font-orbitron font-bold mb-6">Industry Applications</h2>
                  
                  <div className="grid md:grid-cols-2 gap-8">
                    <div className="space-y-6">
                      <div>
                        <h3 className="text-xl font-bold mb-3 text-primary">Warehouses & Distribution</h3>
                        <p className="text-muted-foreground mb-3">
                          Perfect for large-scale warehouse operations requiring heavy payload capacity and multi-floor delivery.
                        </p>
                        <ul className="space-y-2 text-muted-foreground">
                          <li>• Pallet and rack transportation</li>
                          <li>• Cross-dock operations</li>
                          <li>• Multi-floor material handling</li>
                          <li>• Inventory replenishment</li>
                        </ul>
                      </div>

                      <div>
                        <h3 className="text-xl font-bold mb-3 text-primary">Manufacturing Plants</h3>
                        <p className="text-muted-foreground mb-3">
                          Streamline production workflows with automated material delivery between stations.
                        </p>
                        <ul className="space-y-2 text-muted-foreground">
                          <li>• Raw material delivery</li>
                          <li>• Work-in-progress transport</li>
                          <li>• Finished goods handling</li>
                          <li>• Production line supply</li>
                        </ul>
                      </div>
                    </div>

                    <div className="space-y-6">
                      <div>
                        <h3 className="text-xl font-bold mb-3 text-primary">E-Commerce Fulfillment</h3>
                        <p className="text-muted-foreground mb-3">
                          Optimize fulfillment center operations with high-capacity autonomous delivery.
                        </p>
                        <ul className="space-y-2 text-muted-foreground">
                          <li>• Order picking support</li>
                          <li>• Packing station delivery</li>
                          <li>• Returns processing</li>
                          <li>• Inventory management</li>
                        </ul>
                      </div>

                      <div>
                        <h3 className="text-xl font-bold mb-3 text-primary">Logistics Centers</h3>
                        <p className="text-muted-foreground mb-3">
                          Enhance logistics operations with reliable heavy-payload transportation.
                        </p>
                        <ul className="space-y-2 text-muted-foreground">
                          <li>• Dock-to-stock movements</li>
                          <li>• Consolidation operations</li>
                          <li>• Sortation support</li>
                          <li>• Loading area delivery</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </Card>

                <Card className="p-8 glass-effect border-primary/20 bg-gradient-to-br from-primary/5 to-background">
                  <h2 className="text-2xl font-orbitron font-bold mb-4">Why Choose PUDU T600?</h2>
                  <p className="text-muted-foreground mb-6">
                    The PUDU T600 combines industry-leading payload capacity with intelligent automation to transform your warehouse and logistics operations. With two variants available - standard and underride - it adapts to your specific operational needs while delivering unmatched performance and reliability.
                  </p>
                  <div className="flex flex-wrap gap-4">
                    <Button size="lg" asChild className="glow-gold">
                      <a href="https://api.leadconnectorhq.com/widget/bookings/neilaipudurobotics" target="_blank" rel="noopener noreferrer">
                        Schedule Your Demo <ArrowRight className="ml-2" />
                      </a>
                    </Button>
                    <Button size="lg" variant="outline" asChild>
                      <Link to="/warehouses">View Warehouse Solutions</Link>
                    </Button>
                  </div>
                </Card>
              </TabsContent>

              <TabsContent value="action" className="space-y-6">
                <Card className="p-8 glass-effect border-primary/20">
                  <h2 className="text-3xl font-orbitron font-bold mb-6">PUDU T600 In Action</h2>
                  <p className="text-lg text-muted-foreground mb-6">
                    Watch the PUDU T600 demonstrate its heavy-payload capabilities, intelligent navigation, and autonomous operation in real industrial environments.
                  </p>
                  
                  <div className="aspect-video w-full rounded-lg overflow-hidden bg-black/50">
                    <video 
                      controls 
                      className="w-full h-full object-contain"
                      poster={t600Img}
                      preload="metadata"
                      playsInline
                    >
                      <source src={actionVideo} type="video/quicktime" />
                      <source src={actionVideo} type="video/mp4" />
                      Your browser does not support the video tag.
                    </video>
                  </div>

                  <div className="mt-8 text-center">
                    <p className="text-muted-foreground mb-6">
                      See firsthand how the T600 handles 600kg payloads, navigates multi-floor facilities, and seamlessly integrates into warehouse operations with follow mode and autonomous delivery capabilities.
                    </p>
                    <Button size="lg" asChild className="glow-gold">
                      <a href="https://api.leadconnectorhq.com/widget/bookings/neilaipudurobotics" target="_blank" rel="noopener noreferrer">
                        Schedule Your Demo <ArrowRight className="ml-2" />
                      </a>
                    </Button>
                  </div>
                </Card>
              </TabsContent>
            </Tabs>
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
};

export default PuduT600;
